"use client";

import { useState, useEffect, useCallback, useMemo } from "react";

const STORAGE_KEY_PREFIX = "forgeswap:hidden_tokens:";

/**
 * Hook to manage hidden tokens per wallet address.
 * Persists to localStorage so hidden tokens remain hidden across sessions.
 */
export function useHiddenTokens(walletAddress: string | null | undefined) {
  const [hiddenTokenIds, setHiddenTokenIds] = useState<string[]>([]);

  const storageKey = useMemo(
    () => (walletAddress ? `${STORAGE_KEY_PREFIX}${walletAddress}` : null),
    [walletAddress]
  );

  // Load from localStorage on wallet change
  useEffect(() => {
    if (!storageKey) {
      setHiddenTokenIds([]);
      return;
    }

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setHiddenTokenIds(parsed);
          return;
        }
      }
    } catch {
      // Parse error
    }
    setHiddenTokenIds([]);
  }, [storageKey]);

  // Persist to localStorage whenever hiddenTokenIds changes
  const persistToStorage = useCallback(
    (ids: string[]) => {
      if (!storageKey) return;
      try {
        localStorage.setItem(storageKey, JSON.stringify(ids));
      } catch {
        // localStorage full or unavailable
      }
    },
    [storageKey]
  );

  const hideToken = useCallback(
    (mintAddress: string) => {
      if (!mintAddress || !storageKey) return;
      setHiddenTokenIds((prev) => {
        if (prev.includes(mintAddress)) return prev;
        const next = [...prev, mintAddress];
        persistToStorage(next);
        return next;
      });
    },
    [storageKey, persistToStorage]
  );

  const unhideToken = useCallback(
    (mintAddress: string) => {
      if (!mintAddress || !storageKey) return;
      setHiddenTokenIds((prev) => {
        const next = prev.filter((id) => id !== mintAddress);
        persistToStorage(next);
        return next;
      });
    },
    [storageKey, persistToStorage]
  );

  const isHidden = useCallback(
    (mintAddress: string): boolean => {
      return hiddenTokenIds.includes(mintAddress);
    },
    [hiddenTokenIds]
  );

  return {
    hiddenTokenIds,
    hideToken,
    unhideToken,
    isHidden,
    hiddenCount: hiddenTokenIds.length,
  };
}
